import type {Plugin} from 'unified';
import type {Parent, Root} from 'mdast';

// Platform-specific article blocks, authored as adjacent remark-directive
// containers:
//
//   :::web
//   1. Go to **Settings > Language**
//   :::
//   :::mobile
//   1. Open the app and tap **More > Settings**
//   :::
//
// Adjacent :::web / :::mobile containers merge into one synced tab group
// (rendered by PlatformTabs). remark-directive must run before this plugin.
const PLATFORMS = ['web', 'mobile'];

type DirectiveNode = Parent & {type: 'containerDirective'; name: string};

const isPlatformDirective = (node: unknown): node is DirectiveNode => {
  const candidate = node as {type?: string; name?: string};
  return (
    candidate.type === 'containerDirective' &&
    typeof candidate.name === 'string' &&
    PLATFORMS.includes(candidate.name)
  );
};

function transform(parent: Parent) {
  const children = parent.children as unknown[];

  for (let i = 0; i < children.length; i++) {
    if (!isPlatformDirective(children[i])) {
      continue;
    }
    let end = i;
    while (end < children.length && isPlatformDirective(children[end])) {
      end++;
    }
    const run = children.slice(i, end) as DirectiveNode[];
    const tabsNode = {
      type: 'platformTabs',
      data: {hName: 'platform-tabs'},
      children: run.map((directive) => ({
        type: 'platformTab',
        data: {
          hName: 'platform-tab',
          hProperties: {platform: directive.name},
        },
        children: directive.children,
      })),
    };
    children.splice(i, run.length, tabsNode);
  }

  for (const child of children) {
    if (child && typeof child === 'object' && 'children' in child) {
      transform(child as Parent);
    }
  }
}

export const platformTabsPlugin: Plugin<[], Root> = () => {
  return (tree: Root) => {
    transform(tree);
  };
};
